// Portefeuille : tous les projets accessibles, À PLAT.
//
// Le serveur renvoie des clients qui contiennent leurs projets ; on ne montre
// plus cet emboîtement. Une seule grille liste tous les projets, et le client
// n'est plus qu'une étiquette sur chaque carte.
//
// Ce que le serveur renvoie ici est DÉJÀ borné au périmètre de la session — un
// client sans projet visible n'apparaît pas du tout. Le frontend ne filtre donc
// rien : filtrer côté client supposerait avoir reçu ce qu'on n'a pas le droit
// de voir.
import { useMemo, useState } from 'react';
import { useBootstrap } from '../api/queries';
import type { Project } from '../api/types';
import { Button, EmptyState, ErrorBanner, Eyebrow, Spinner } from '../components/ui';
import { Link } from '../router';
import { Icon, type IconName } from '../components/icons';
import { initialsOf } from '../components/AppShell';
import { hueClass, huesInOrder, type Hue } from '../taxonomy/colors';
import { CreateProjectAction, ProjectActions } from './PortfolioForms';
import { ResearchIllustration } from './Illustration';
import { StatusChip } from '../taxonomy/Chips';
import { savePreference } from '../legal/cookieConsent';
import './portfolio.css';

/** Un projet et le nom de son client : la ligne à plat du portefeuille. */
interface PortfolioRow {
  project: Project;
  clientName: string;
  /** Teinte du client : c'est elle qui regroupe ses projets à l'œil. */
  hue: Hue;
}

function ScoreBadge({ score }: { score: number | null }) {
  // `null` n'est pas zéro : un projet dont le score n'est pas calculé ne vaut
  // pas « 0 / 100 ». C'est la même règle que le moteur de notation côté serveur.
  if (score === null || score === undefined) {
    return (
      <div className="score score-empty">
        <span className="score-value">—</span>
        <span className="score-label">non calculé</span>
      </div>
    );
  }
  return (
    <div className="score">
      <span className="score-value">{Math.round(score)}</span>
      <span className="score-unit">/100</span>
    </div>
  );
}

function ProjectCard({ row, canManage }: { row: PortfolioRow; canManage: boolean }) {
  const { project, clientName, hue } = row;
  const meta = [
    project.etudes !== null ? `${project.etudes} étude${project.etudes > 1 ? 's' : ''}` : null,
    project.activite,
  ].filter(Boolean);

  return (
    <li className="project-card">
      <Link to={`/projet/${encodeURIComponent(project.id)}`} className="project">
        <span className="project-head">
          {/* Le monogramme du client, dans SA teinte : depuis que le portefeuille
              est à plat, c'est la couleur qui regroupe les projets d'un même client. */}
          <span className={`client-mark ${hueClass(hue)}`} aria-hidden="true">
            {initialsOf(clientName)}
          </span>
          <span className="project-id">
            <Eyebrow>{clientName}</Eyebrow>
            <span className="t-h3 project-name">{project.nom}</span>
          </span>
          <ScoreBadge score={project.score} />
        </span>
        <StatusChip value={project.statut} className="status-pill" />
      </Link>
      <div className="project-card-foot">
        <span className="project-meta">{meta.join(' · ')}</span>
        {/* Hors du lien, et pas dedans : un bouton imbriqué dans une ancre est du
            HTML invalide, et surtout un piège — on cliquerait « supprimer » en
            croyant ouvrir le projet. */}
        {canManage ? <ProjectActions project={project} /> : null}
      </div>
    </li>
  );
}

// --- Vue LISTE ---------------------------------------------------------------
//
// Un vrai <table> : colonnes alignées d'un projet à l'autre, lisible au lecteur
// d'écran (en-têtes annoncés), et comparable d'un coup d'œil — ce que la vue en
// cartes ne permet plus quand le portefeuille grossit. Le nom reste un VRAI lien
// (ouvrable dans un nouvel onglet) ; la suppression reste hors du lien.
//
// Le TRI se fait ici, dans le navigateur : toutes les lignes sont déjà là, il
// n'y a rien à redemander au serveur. « Dernière activité » n'est pas triable :
// c'est un texte libre (« il y a 2 jours »), le trier par ordre alphabétique
// donnerait un ordre qui a l'air juste et qui est faux.
type SortKey = 'projet' | 'client' | 'statut' | 'etudes' | 'score';
interface Sort {
  key: SortKey;
  dir: 'asc' | 'desc';
}

const SORT_VALUE: Record<SortKey, (r: PortfolioRow) => string | number | null> = {
  projet: (r) => r.project.nom,
  client: (r) => r.clientName,
  statut: (r) => r.project.statut,
  etudes: (r) => r.project.etudes,
  score: (r) => r.project.score,
};

function sortRows(rows: PortfolioRow[], sort: Sort | null): PortfolioRow[] {
  if (!sort) return rows;
  const value = SORT_VALUE[sort.key];
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = value(a) ?? null;
    const vb = value(b) ?? null;
    // Une valeur absente va TOUJOURS en bas, dans les deux sens : un score non
    // calculé n'est ni le plus petit ni le plus grand.
    if (va === null || vb === null) return va === vb ? 0 : va === null ? 1 : -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
    // `fr` + `numeric` : « é » se range avec « e », et « Projet 2 » avant « Projet 10 ».
    return String(va).localeCompare(String(vb), 'fr', { sensitivity: 'base', numeric: true }) * sign;
  });
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sort: Sort | null;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const dir = sort?.key === sortKey ? sort.dir : null;
  return (
    <th
      scope="col"
      className={className}
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
    >
      {/* Un vrai bouton dans l'en-tête : atteignable au clavier, et le sens du
          tri est annoncé par `aria-sort` sur la colonne. */}
      <button type="button" className="zy-tri" onClick={() => onSort(sortKey)}>
        {label}
      </button>
    </th>
  );
}

function ProjectTable({ rows, canManage }: { rows: PortfolioRow[]; canManage: boolean }) {
  // Sans tri choisi, on garde l'ordre du serveur (clients par nom, puis projets).
  const [sort, setSort] = useState<Sort | null>(null);
  // Premier clic : croissant. Second clic sur la même colonne : décroissant.
  const onSort = (key: SortKey) =>
    setSort((s) => ({ key, dir: s?.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const head = { sort, onSort };

  return (
    <div className="zy-table-cadre project-table-wrap">
      <table className="zy-table project-table">
        <thead>
          <tr>
            <SortHeader label="Projet" sortKey="projet" {...head} />
            <SortHeader label="Client" sortKey="client" {...head} />
            <SortHeader label="Statut" sortKey="statut" {...head} />
            <SortHeader label="Études" sortKey="etudes" className="num" {...head} />
            <SortHeader label="Adoption Score" sortKey="score" className="num" {...head} />
            <th scope="col" className="col-activity">Dernière activité</th>
            {canManage ? (
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {sorted.map(({ project: p, clientName, hue }) => (
            <tr key={p.id}>
              <th scope="row">
                <Link to={`/projet/${encodeURIComponent(p.id)}`} className="project-row-link">
                  {p.nom}
                </Link>
              </th>
              <td className="col-client">
                <span className={`client-dot hue-${hue}`} aria-hidden="true" />
                {clientName}
              </td>
              <td>
                <StatusChip value={p.statut} className="status-pill" />
              </td>
              <td className="num">{p.etudes ?? '—'}</td>
              <td className="num">
                {/* `null` n'est pas zéro : même règle que la carte. */}
                {p.score === null || p.score === undefined ? (
                  <span className="table-empty">non calculé</span>
                ) : (
                  <>
                    <strong>{Math.round(p.score)}</strong>
                    <span className="table-unit">/100</span>
                  </>
                )}
              </td>
              <td className="col-activity">{p.activite ?? '—'}</td>
              {canManage ? (
                <td className="actions">
                  <ProjectActions project={p} />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type ViewMode = 'cards' | 'list';
const VIEW_KEY = 'ro.portfolio.view';

// Préférence PAR NAVIGATEUR, simple confort : le stockage peut être indisponible
// (navigation privée, stockage bloqué) — la page s'affiche alors en cartes.
function readView(): ViewMode {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'cards';
  } catch {
    return 'cards';
  }
}

function writeView(v: ViewMode) {
  savePreference(VIEW_KEY, v);
}

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  return (
    <div className="view-toggle" role="group" aria-label="Affichage des projets">
      <button type="button" aria-pressed={value === 'cards'} onClick={() => onChange('cards')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
        Cartes
      </button>
      <button type="button" aria-pressed={value === 'list'} onClick={() => onChange('list')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
        Liste
      </button>
    </div>
  );
}

export function PortfolioPage() {
  const bootstrap = useBootstrap(true);
  const [view, setView] = useState<ViewMode>(readView);
  const changeView = (v: ViewMode) => {
    setView(v);
    writeView(v);
  };

  // La mise à plat : chaque projet emporte le nom de son client. L'ordre du
  // serveur est conservé (clients par nom, puis leurs projets).
  const rows = useMemo<PortfolioRow[]>(() => {
    const clients = bootstrap.data?.clients ?? [];
    const hues = huesInOrder(clients.map((c) => c.nom));
    return clients.flatMap((c) =>
      c.projets.map((project) => ({ project, clientName: c.nom, hue: hues.get(c.nom) ?? 'cyan' })),
    );
  }, [bootstrap.data]);
  const totalProjects = rows.length;

  if (bootstrap.isPending) {
    return (
      <div className="app-loading">
        <Spinner label="Chargement de votre portefeuille…" />
      </div>
    );
  }

  if (bootstrap.isError) {
    return (
      <div className="app-main">
        <ErrorBanner>
          {bootstrap.error instanceof Error
            ? bootstrap.error.message
            : 'Impossible de charger le portefeuille.'}
        </ErrorBanner>
        <Button variant="ghost" onClick={() => void bootstrap.refetch()}>
          Réessayer
        </Button>
      </div>
    );
  }

  const { clients } = bootstrap.data;
  // La gouvernance du portefeuille (créer un projet, et son client au passage)
  // est réservée aux admins d'organisation. Le serveur refuse les autres en
  // 403 ; on se contente de ne pas leur montrer le déclencheur.
  const canManage = bootstrap.data.user.orgRole === 'admin';
  const clientCount = new Set(rows.map((r) => r.clientName)).size;

  // Les chiffres de l'accueil. Le score moyen ne compte que les projets NOTÉS :
  // un projet non calculé ne vaut pas zéro.
  const studies = rows.reduce((n, r) => n + (r.project.etudes ?? 0), 0);
  const scored = rows.map((r) => r.project.score).filter((x): x is number => x !== null && x !== undefined);
  const average = scored.length > 0 ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length) : null;
  const stats: { icon: IconName; hue: Hue; value: string; label: string }[] = [
    { icon: 'folder', hue: 'cyan', value: String(totalProjects), label: totalProjects > 1 ? 'Projets' : 'Projet' },
    { icon: 'building', hue: 'violet', value: String(clientCount), label: clientCount > 1 ? 'Clients' : 'Client' },
    { icon: 'flask', hue: 'orange', value: String(studies), label: studies > 1 ? 'Études' : 'Étude' },
    { icon: 'gauge', hue: 'green', value: average === null ? '—' : String(average), label: 'Adoption moyenne' },
  ];
  const firstName = (bootstrap.data.user.name || '').trim().split(/\s+/)[0] ?? '';

  return (
    <main className="app-main">
      <section className="home-hero" aria-labelledby="home-title">
        <div className="home-hero-text">
          <Eyebrow>Portefeuille</Eyebrow>
          <h1 className="t-h1" id="home-title">
            Bonjour{firstName ? ` ${firstName}` : ''}
          </h1>
          <p className="zy-intro">
            {totalProjects === 0
              ? 'Votre espace est prêt. Créez un premier projet pour y verser des études, des verbatims et des insights.'
              : 'Suivez l’adoption de vos produits, et sachez pourquoi elle bouge.'}
          </p>
          {totalProjects > 0 ? (
            <ul className="home-stats">
              {stats.map((st) => (
                <li key={st.label} className="home-stat">
                  <span className={`home-stat-icon hue-${st.hue}`}>
                    <Icon name={st.icon} size={17} />
                  </span>
                  <span>
                    <strong>{st.value}</strong>
                    <span className="home-stat-label">{st.label}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <ResearchIllustration />
      </section>

      {totalProjects > 0 ? (
        <div className="zy-page-head">
          <h2 className="t-h2">Projets</h2>
          <div className="zy-page-head__actions">
            <ViewToggle value={view} onChange={changeView} />
            {canManage ? <CreateProjectAction clients={clients} /> : null}
          </div>
        </div>
      ) : null}

      {totalProjects === 0 ? (
        <EmptyState
          title="Aucun projet pour l’instant"
          description="Un projet, c’est un produit ou un chantier étudié pour un client. Son nom et son client suffisent pour démarrer."
          {...(canManage ? { action: <CreateProjectAction clients={clients} /> } : {})}
        />
      ) : view === 'list' ? (
        <ProjectTable rows={rows} canManage={canManage} />
      ) : (
        <ul className="project-grid">
          {rows.map((row) => (
            <ProjectCard key={row.project.id} row={row} canManage={canManage} />
          ))}
        </ul>
      )}
    </main>
  );
}
